<!-- Bauform bewusst identisch zum Moodle-Verbindungsdialog: Kopfzeile,
     Zustandszeile, Kachelraster mit Einstellungsgruppen, Fusszeile. Das
     gemeinsame Aussehen steht in `_lms-connect-dialog.scss`. -->
<nb-card class="form" [class.form--wide]="hasConnections">
  <nb-card-header>
    <h4>{{ 'dialogs.iliasConnect.title' | translate }}</h4>
  </nb-card-header>

  <nb-card-body>
    <div *ngIf="loading" class="loading-state">
      <nb-spinner status="primary" size="medium"></nb-spinner>
    </div>

    <div *ngIf="!loading" class="connected-state">
      <!-- Zwei Wege, einer sichtbar. Vorher standen beide untereinander —
           der Plugin-Kasten, darunter die zehn SOAP-Schritte, darunter in
           jeder Verbindung der technische Zugang. Wer sich fuer einen Weg
           entschieden hatte, las trotzdem weiter die Teile des anderen und
           konnte nicht erkennen, was fuer ihn gilt. -->
      <div class="path-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="path-tabs__tab"
          [class.is-active]="pathTab === 'plugin'"
          [attr.aria-selected]="pathTab === 'plugin'"
          (click)="setPathTab('plugin')"
        >
          <nb-icon icon="flash-outline"></nb-icon>
          {{ 'dialogs.iliasConnect.path.plugin' | translate }}
        </button>
        <button
          type="button"
          role="tab"
          class="path-tabs__tab"
          [class.is-active]="pathTab === 'legacy'"
          [attr.aria-selected]="pathTab === 'legacy'"
          (click)="setPathTab('legacy')"
        >
          <nb-icon icon="book-open-outline"></nb-icon>
          {{ 'dialogs.iliasConnect.path.legacy' | translate }}
        </button>
      </div>
      <p class="path-tabs__note">
        {{ (pathTab === 'plugin'
              ? 'dialogs.iliasConnect.path.pluginNote'
              : 'dialogs.iliasConnect.path.legacyNote') | translate }}
      </p>

      <!-- Ohne Verbindung beginnt der Weg hier, nicht an der LTI-Kachel:
           eine reine Plugin-Kopplung braucht kein LTI. Die Kachel bleibt als
           zweiter Weg stehen, fuer Haeuser, die kein Plugin einspielen
           duerfen. -->
      <div *ngIf="!hasConnections" class="status-row status-row--secondary">
        <nb-icon icon="info-outline" status="basic"></nb-icon>
        <div>
          <p class="status-label">{{ 'dialogs.iliasConnect.noConnection' | translate }}</p>
          <p class="status-url">{{ 'dialogs.iliasConnect.noConnectionHint' | translate }}</p>
          <button
            nbButton
            size="small"
            status="basic"
            class="no-connection-action"
            (click)="dialogRef.close('open-lti')"
          >
            {{ 'dialogs.iliasConnect.noConnectionAction' | translate }}
          </button>
        </div>
      </div>

      <!-- Der Plugin-Weg. Steht über der Anleitung, weil er sie ersetzt:
           ein Code statt zehn Schritte. Wer bereits gekoppelt ist, sieht hier
           den Zustand und nicht mehr die Einrichtung.

           Bewusst NICHT an `hasConnections` gebunden: eine reine
           Plugin-Kopplung entsteht ohne LTI-Konsumenten und legt ihre
           Verbindungszeile selbst an (`source='ilias_plugin'`). Solange der
           Kasten eine Verbindung voraussetzte, war genau dieser Weg aus dem
           Portal nicht erreichbar. -->
      <div class="plugin-box" *ngIf="pathTab === 'plugin'" [class.is-paired]="hasPairedPlugin">
        <!-- Kopfzeile klickbar, wie bei der Einrichtungsanleitung darunter:
             der Kasten steht auf jeder Ansicht dieses Dialogs, aber nur wer
             gerade verbindet, braucht die Schritte. Ist bereits gekoppelt,
             bleibt der Zustand auch eingeklappt sichtbar. -->
        <button type="button" class="plugin-box__head" (click)="togglePlugin()">
          <nb-icon icon="flash-outline" class="plugin-box__icon"></nb-icon>
          <div class="plugin-box__title">
            <strong>{{ 'dialogs.iliasConnect.plugin.title' | translate }}</strong>
            <p>{{ 'dialogs.iliasConnect.plugin.subtitle' | translate }}</p>
          </div>
          <nb-icon
            class="plugin-box__chevron"
            [icon]="pluginOpen ? 'chevron-up-outline' : 'chevron-down-outline'"
          ></nb-icon>
        </button>

        <!-- Gekoppelt: was das Plugin zuletzt gemeldet hat. Version und
             Fähigkeiten erntet das Backend aus jedem signierten Eingang, hier
             steht also der tatsächliche Stand, kein gespeicherter Wunsch. -->
        <ng-container *ngFor="let c of connections">
          <div *ngIf="plugin(c) as p" class="plugin-box__state">
            <span class="state-chip is-connected">
              {{ 'dialogs.iliasConnect.plugin.paired' | translate }}
            </span>
            <span class="state-chip" *ngIf="p.version">v{{ p.version }}</span>
            <span class="state-chip" *ngIf="p.ilias_version">ILIAS {{ p.ilias_version }}</span>
            <span class="state-chip" *ngFor="let cap of p.capabilities">
              {{ 'dialogs.iliasConnect.plugin.cap.' + cap | translate }}
            </span>
            <span class="plugin-box__seen" *ngIf="p.last_seen_at">
              {{ 'dialogs.iliasConnect.plugin.lastSeen' | translate }}
              {{ p.last_seen_at | date: 'dd.MM.yyyy HH:mm' }}
            </span>
          </div>
        </ng-container>

        <!-- Noch nicht gekoppelt: Code erzeugen. Er erscheint genau einmal —
             danach liegt nur sein Hash in der Datenbank. -->
        <!-- Aufklappbar auch dann, wenn schon gekoppelt ist. Vorher hing der
             ganze Inhalt an „noch nicht verbunden", der Pfeil bewegte sich
             also, ohne dass etwas erschien — und wer die Schritte, das Paket
             oder das Changelog nachschlagen wollte, kam nicht mehr daran. -->
        <div *ngIf="pluginOpen" class="plugin-box__setup">
          <ul class="plugin-box__benefits">
            <li>{{ 'dialogs.iliasConnect.plugin.does1' | translate }}</li>
            <li>{{ 'dialogs.iliasConnect.plugin.does2' | translate }}</li>
            <li>{{ 'dialogs.iliasConnect.plugin.does3' | translate }}</li>
          </ul>

          <h6 class="plugin-box__steps-title">
            {{ 'dialogs.iliasConnect.plugin.stepsTitle' | translate }}
          </h6>
          <ol class="plugin-box__steps">
            <li [innerHTML]="'dialogs.iliasConnect.plugin.step1' | translate"></li>
            <li [innerHTML]="'dialogs.iliasConnect.plugin.step2' | translate"></li>
            <li [innerHTML]="'dialogs.iliasConnect.plugin.step3' | translate"></li>
            <li [innerHTML]="'dialogs.iliasConnect.plugin.step4' | translate"></li>
          </ol>

          <!-- Die Versionsbindung ist hart; wer eine 10er betreibt, soll es
               vor dem Herunterladen erfahren und nicht erst beim Aktivieren. -->
          <p class="plugin-box__codeinfo">
            {{ 'dialogs.iliasConnect.plugin.versions' | translate }}
          </p>

          <!-- Ohne diese drei Adressen kann niemand anfangen: das Paket liegt
               nicht im Portal, und ILIAS hat keinen Marktplatz, aus dem man es
               zoege. -->
          <p class="plugin-box__links">
            <a [href]="pluginDownloadUrl" target="_blank" rel="noopener">
              <nb-icon icon="download-outline"></nb-icon>
              {{ 'dialogs.iliasConnect.plugin.download' | translate }}
            </a>
            <a [href]="pluginGuideUrl" target="_blank" rel="noopener">
              {{ 'dialogs.iliasConnect.plugin.guide' | translate }}
            </a>
            <a [href]="pluginChangelogUrl" target="_blank" rel="noopener">
              {{ 'dialogs.iliasConnect.plugin.changelog' | translate }}
            </a>
          </p>

          <p class="plugin-box__codeinfo">
            {{ 'dialogs.iliasConnect.plugin.codeExplainer' | translate }}
          </p>
          <!-- Der haeufigste Grund, warum ein erzeugter Code nirgends
               eingegeben werden kann: die Installation haengt schon an einem
               Konto, und das Plugin zeigt dann gar kein Code-Feld mehr. Von
               hier aus ist das nicht sichtbar — eine fremde Kopplung steht in
               einem anderen Mandanten. -->
          <p class="plugin-box__codeinfo">
            {{ 'dialogs.iliasConnect.plugin.alreadyPairedHint' | translate }}
          </p>

          <div *ngIf="!pluginCode && !hasPairedPlugin" class="plugin-box__action">
            <button
              nbButton
              size="small"
              status="primary"
              [disabled]="generatingCode"
              (click)="generatePluginCode()"
            >
              <nb-spinner *ngIf="generatingCode" size="tiny"></nb-spinner>
              {{ 'dialogs.iliasConnect.plugin.generate' | translate }}
            </button>
          </div>

          <div *ngIf="pluginCode && !hasPairedPlugin" class="plugin-box__code">
            <code>{{ pluginCode }}</code>
            <button
              nbButton
              size="small"
              status="basic"
              (click)="copyPluginCode()"
            >
              <nb-icon [icon]="codeCopied ? 'checkmark-outline' : 'copy-outline'"></nb-icon>
              {{ (codeCopied
                    ? 'dialogs.iliasConnect.plugin.copiedShort'
                    : 'dialogs.iliasConnect.plugin.copy') | translate }}
            </button>
            <p class="plugin-box__once">
              {{ 'dialogs.iliasConnect.plugin.onceHint' | translate }}
              <span *ngIf="pluginCodeExpires">
                {{ 'dialogs.iliasConnect.plugin.expires' | translate }}
                {{ pluginCodeExpires | date: 'dd.MM.yyyy HH:mm' }}
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Einrichtungsanleitung für den SOAP-Weg — der zweite Weg, nicht der
           erste. Eingeklappt und ausdrücklich als Rückfall beschriftet: wer
           ein Plugin einspielen darf, soll hier gar nicht erst anfangen. Ist
           ein Plugin gekoppelt, entfällt sie ganz — dann hat die Installation
           alles, was die zehn Schritte herstellen sollten. -->
      <div *ngIf="pathTab === 'legacy'" class="guide-box" [class.is-open]="guideOpen">
        <button type="button" class="guide-box__head" (click)="toggleGuide()">
          <nb-icon icon="book-open-outline" class="guide-box__icon"></nb-icon>
          <span class="guide-box__label">
            {{ 'dialogs.iliasConnect.guideFallbackTitle' | translate: { count: guideStepNumbers.length } }}
          </span>
          <nb-icon
            class="guide-box__chevron"
            [icon]="guideOpen ? 'chevron-up-outline' : 'chevron-down-outline'"
          ></nb-icon>
        </button>
        <div *ngIf="guideOpen" class="guide-box__body">
          <!-- Was dieser Weg kostet, steht vor dem ersten Schritt. Wer es
               erst nach Schritt sechs merkt, hat den Server-Zugang bereits
               beantragt. -->
          <p class="guide-box__fallback-note">
            {{ 'dialogs.iliasConnect.guideFallbackNote' | translate }}
          </p>
          <!-- Die Schritte zitieren ILIAS-Beschriftungen woertlich. Laeuft die
               Instanz auf Englisch, heissen sie dort anders — der Umschalter
               tauscht genau diese Begriffe aus, nicht die Sprache der
               Anleitung. -->
          <div class="guide-box__lang">
            <span class="guide-box__lang-label">
              {{ 'dialogs.iliasConnect.iliasLangLabel' | translate }}
            </span>
            <button
              type="button"
              class="guide-box__lang-btn"
              [class.is-active]="iliasLang === 'de'"
              (click)="setIliasLang('de')"
            >DE</button>
            <button
              type="button"
              class="guide-box__lang-btn"
              [class.is-active]="iliasLang === 'en'"
              (click)="setIliasLang('en')"
            >EN</button>
          </div>

          <ol class="guide">
            <!-- innerHTML, damit ein Schritt seine Teilschritte als Liste
                 zeigen kann statt als Bandwurmsatz. Der Inhalt kommt aus
                 unserer eigenen i18n-Datei, nicht von aussen. -->
            <li
              *ngFor="let step of guideStepNumbers"
              [innerHTML]="'dialogs.iliasConnect.guide.s' + step | translate: iliasTerms"
            ></li>
          </ol>
          <p class="guide-box__caveat">{{ 'dialogs.iliasConnect.guide.caveat' | translate }}</p>
        </div>
      </div>

      <div *ngIf="hasConnections" class="sync-settings">
        <div
          *ngFor="let c of connections"
          class="site-row"
          [class.is-connected-row]="isConnected(c)"
        >
          <div class="site-row__header">
            <span class="site-row__favicon"><nb-icon icon="globe-outline"></nb-icon></span>
            <div class="site-row__identity">
              <strong>{{ c.display_name || c.issuer }}</strong>
              <span>{{ c.issuer }}</span>
            </div>
            <span
              class="site-row__dot"
              [class.is-connected]="isConnected(c)"
              [class.is-paused]="c.paused"
              [class.is-off]="c.connection_status === 'disconnected'"
            ></span>
          </div>

          <div class="site-row__chips">
            <span class="state-chip" [ngClass]="connectionChipClass(c)">
              {{ connectionChipKey(c) | translate }}
            </span>
            <span class="state-chip" *ngIf="c.platform_version">
              ILIAS {{ c.platform_version }}
            </span>
            <span class="state-chip" [class.is-primary]="c.environment !== 'staging'">
              {{ (c.environment === 'staging'
                    ? 'dialogs.iliasConnect.envStaging'
                    : 'dialogs.iliasConnect.envProduction') | translate }}
            </span>
            <!-- Sagt, welcher Weg diese Verbindung traegt. Ohne ihn stand in
                 der Karte nie, ob gerade das Plugin arbeitet oder LTI+SOAP —
                 und genau das ist der Unterschied, an dem alles haengt. -->
            <span class="state-chip is-channel">
              {{ channelChipKey(c) | translate }}
            </span>
            <!-- SOAP nur nennen, wo SOAP eine Rolle spielt. -->
            <span class="state-chip" *ngIf="usesSoap(c)" [ngClass]="soapChipClass(c)">
              {{ soapChipKey(c) | translate }}
            </span>
          </div>

          <!-- Zu alte Installation: vor ILIAS 11 arbeitet die dynamische
               Registrierung nicht spezifikationskonform. -->
          <div *ngIf="isOutdated(c)" class="site-info site-info--warning">
            <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
            <div class="site-info__body">
              <strong>{{ 'dialogs.iliasConnect.outdated' | translate }}</strong>
              <p>{{ 'dialogs.iliasConnect.outdatedHint' | translate }}</p>
            </div>
          </div>

          <div
            *ngIf="pathTab === 'legacy' && !soapConfigured(c) && !pluginCoversData(c)"
            class="site-info site-info--warning"
          >
            <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
            <div class="site-info__body">
              <strong>{{ 'dialogs.iliasConnect.soap.needed' | translate }}</strong>
              <p>{{ 'dialogs.iliasConnect.soap.neededHint' | translate }}</p>
            </div>
          </div>

          <div *ngIf="lastError(c) as err" class="site-info site-info--danger">
            <nb-icon icon="alert-triangle-outline" status="danger"></nb-icon>
            <div class="site-info__body">
              <strong>{{ 'dialogs.iliasConnect.soap.status.error' | translate }}</strong>
              <p>{{ err }}</p>
            </div>
          </div>

          <div class="site-settings">
            <!-- ─── Verbindung ─────────────────────────────────────── -->
            <div class="settings-group">
              <p class="settings-group__title">
                {{ 'dialogs.iliasConnect.groupConnection' | translate }}
              </p>

              <div
                class="settings-row"
                [nbTooltip]="pauseRowTooltipKey(c) | translate"
              >
                <!-- Drei Zustaende, nicht zwei. „Getrennt" sah in der
                     Datenbank aus wie „pausiert" (beide setzen `disabled_at`),
                     und die Zeile bot deshalb „Verbindung fortsetzen" an —
                     bei einem Knopf, der gesperrt ist, weil Fortsetzen hier
                     gar nicht der richtige Weg waere. -->
                <span class="settings-row__label">
                  {{ pauseRowLabelKey(c) | translate }}
                  <nb-icon class="settings-row__help" icon="question-mark-circle-outline"></nb-icon>
                </span>
                <button
                  nbButton
                  size="small"
                  outline
                  [status]="c.connection_status === 'disconnected'
                    ? 'basic'
                    : (c.paused ? 'success' : 'warning')"
                  [disabled]="busy[c.id] || c.connection_status === 'disconnected'"
                  [nbSpinner]="busy[c.id]"
                  (click)="togglePause(c)"
                  [attr.aria-label]="(c.paused
                    ? 'dialogs.iliasConnect.resume'
                    : 'dialogs.iliasConnect.pause') | translate"
                >
                  <nb-icon [icon]="c.paused ? 'play-circle-outline' : 'pause-circle-outline'"></nb-icon>
                </button>
              </div>
            </div>

            <!-- ─── Technischer Zugang ─────────────────────────────── -->
            <!-- Nur wo SOAP den Datenweg traegt. Liefert das Plugin Objekte
                 und Mitglieder, ist dieser ganze Block ohne Wirkung — er
                 stand trotzdem da, mit Eingabefeldern, einem Pruefknopf und
                 einem eigenen Zustandschip. Nichts davon gehoerte zu dem
                 Weg, den diese Verbindung tatsaechlich geht. -->
            <div class="settings-group" *ngIf="pathTab === 'legacy' && usesSoap(c)">
              <p class="settings-group__title">
                {{ 'dialogs.iliasConnect.groupAccess' | translate }}
              </p>

              <!-- Beide Kanaele gleichzeitig: dann ist SOAP der Rest, den das
                   Plugin nicht abdeckt, und das gehoert dazugesagt. -->
              <p class="settings-group__note" *ngIf="pluginActive(c)">
                {{ 'dialogs.iliasConnect.soap.redundantHint' | translate }}
              </p>

              <div class="ilias-fields" *ngIf="forms[c.id] as form">
                <label class="ilias-field">
                  <span>{{ 'dialogs.iliasConnect.soap.url' | translate }}</span>
                  <input nbInput fullWidth fieldSize="small" [(ngModel)]="form.soap_url" />
                </label>
                <label class="ilias-field">
                  <span>{{ 'dialogs.iliasConnect.soap.client' | translate }}</span>
                  <input nbInput fullWidth fieldSize="small" placeholder="default" [(ngModel)]="form.ilias_client" />
                </label>
                <label class="ilias-field">
                  <span>{{ 'dialogs.iliasConnect.soap.username' | translate }}</span>
                  <input nbInput fullWidth fieldSize="small" [(ngModel)]="form.username" />
                </label>
                <label class="ilias-field">
                  <span>{{ 'dialogs.iliasConnect.soap.password' | translate }}</span>
                  <input
                    nbInput
                    fullWidth
                    fieldSize="small"
                    type="password"
                    autocomplete="new-password"
                    [(ngModel)]="form.password"
                    [placeholder]="(soap[c.id]?.has_password
                      ? 'dialogs.iliasConnect.soap.passwordKept'
                      : 'dialogs.iliasConnect.soap.passwordNew') | translate"
                  />
                </label>
                <label class="ilias-field">
                  <span>{{ 'dialogs.iliasConnect.soap.template' | translate }}</span>
                  <input nbInput fullWidth fieldSize="small" type="number" placeholder="—" [(ngModel)]="form.template_ref_id" />
                </label>
              </div>

              <p class="settings-row__hint">{{ 'dialogs.iliasConnect.soap.hint' | translate }}</p>

              <div class="ilias-actions">
                <button nbButton status="primary" size="small" [disabled]="busy[c.id]" (click)="saveSoap(c)">
                  {{ (busy[c.id] ? 'buttons.saving' : 'buttons.save') | translate }}
                </button>
                <button
                  nbButton
                  status="basic"
                  size="small"
                  [disabled]="testing[c.id] || !soapConfigured(c)"
                  (click)="testSoap(c)"
                >
                  {{ (testing[c.id] ? 'buttons.saving' : 'dialogs.iliasConnect.soap.test') | translate }}
                </button>
                <small *ngIf="soap[c.id]?.last_ok_at">
                  {{ 'dialogs.iliasConnect.soap.lastOk' | translate }}:
                  {{ soap[c.id]?.last_ok_at | date: 'short' }}
                </small>
              </div>
            </div>

            <!-- ─── Synchronisation ────────────────────────────────── -->
            <!-- Eine Gruppe wie bei Moodle: alles, was regelmäßig läuft,
                 steht beieinander. -->
            <div class="settings-group">
              <p class="settings-group__title">
                {{ 'dialogs.iliasConnect.groupSync' | translate }}
              </p>

              <div class="settings-row" [nbTooltip]="'dialogs.iliasConnect.sync.piiTooltip' | translate">
                <span class="settings-row__label">
                  {{ 'dialogs.iliasConnect.sync.piiLabel' | translate }}
                  <nb-icon class="settings-row__help" icon="question-mark-circle-outline"></nb-icon>
                </span>
                <nb-toggle
                  size="small"
                  status="primary"
                  [checked]="!!settings(c).pii_enabled"
                  [disabled]="!soapConfigured(c) || busy[c.id]"
                  (checkedChange)="onPiiToggle(c, $event)"
                ></nb-toggle>
              </div>

              <p class="settings-row__hint" *ngIf="settings(c).pii_enabled && settings(c).pii_last_synced_at">
                {{ 'dialogs.iliasConnect.sync.piiLastSync' | translate }}:
                {{ settings(c).pii_last_synced_at | date: 'short' }}
              </p>

              <!-- Woran die Zuordnung Lernender haengt. Steht hier, weil der
                   Schalter darueber genau die Felder betrifft, die im
                   Rueckfall gebraucht werden. Zugeklappt: wer es nicht
                   braucht, liest es nicht. -->
              <details class="settings-note">
                <summary>{{ 'dialogs.iliasConnect.mapping.title' | translate }}</summary>
                <p>{{ 'dialogs.iliasConnect.mapping.body' | translate }}</p>
                <p>{{ 'dialogs.iliasConnect.mapping.fallback' | translate }}</p>
              </details>

              <!-- Kurse, Mitglieder und Lehrende werden immer gelesen. Hier
                   standen dafuer ein Schalter und eine Umfangswahl; beides ist
                   weg. Ohne diese Daten zeigt das Portal eine Uebersicht, die
                   keine ist — und die Einschraenkung „nur platzierte Kurse"
                   lieferte Kurslisten ohne Teilnehmende, ohne dass irgendwo
                   stand warum. Waehlbar ist, was an Kursinhalten kommt.
                   Moodle haelt es genauso. -->
              <p class="settings-group__note">
                {{ 'dialogs.iliasConnect.sync.alwaysNote' | translate }}
              </p>

              <div class="settings-row">
                <span class="settings-row__label">
                  {{ 'dialogs.iliasConnect.sync.rootLabel' | translate }}
                  <nb-icon
                    class="settings-row__help"
                    icon="question-mark-circle-outline"
                    [nbTooltip]="'dialogs.iliasConnect.sync.rootTooltip' | translate"
                  ></nb-icon>
                </span>
                <input
                  nbInput
                  fieldSize="small"
                  type="number"
                  class="ilias-root"
                  placeholder="1"
                  [value]="settings(c).root_ref_id || ''"
                  [disabled]="busy[c.id]"
                  (change)="onRootRefIdChange(c, $any($event.target).value)"
                />
              </div>

              <div class="settings-row" [nbTooltip]="'dialogs.iliasConnect.content.scopeTooltip' | translate">
                <span class="settings-row__label">
                  {{ 'dialogs.iliasConnect.content.scopeLabel' | translate }}
                  <nb-icon class="settings-row__help" icon="question-mark-circle-outline"></nb-icon>
                </span>
                <nb-select
                  size="small"
                  [selected]="contentScope(c)"
                  [disabled]="!soapConfigured(c) || busy[c.id]"
                  (selectedChange)="onContentScopeChange(c, $event)"
                >
                  <nb-option value="off">{{ 'dialogs.iliasConnect.content.scopeOff' | translate }}</nb-option>
                  <nb-option value="placements">{{ 'dialogs.iliasConnect.content.scopePlacements' | translate }}</nb-option>
                  <nb-option value="all">{{ 'dialogs.iliasConnect.content.scopeAll' | translate }}</nb-option>
                </nb-select>
              </div>

              <p class="settings-row__hint" [innerHTML]="'dialogs.iliasConnect.content.quotaHint' | translate"></p>

              <div class="settings-row" *ngIf="contentScope(c) !== 'off'">
                <span class="settings-row__label">
                  {{ 'dialogs.iliasConnect.content.structureLabel' | translate }}
                  <nb-icon
                    class="settings-row__help"
                    icon="question-mark-circle-outline"
                    [nbTooltip]="'dialogs.iliasConnect.content.structureTooltip' | translate"
                  ></nb-icon>
                </span>
                <nb-toggle
                  size="small"
                  status="primary"
                  [checked]="!!settings(c).structure_parse"
                  [disabled]="busy[c.id]"
                  (checkedChange)="onStructureParseChange(c, $event)"
                ></nb-toggle>
              </div>

              <p class="settings-row__hint" *ngIf="settings(c).last_run_at">
                {{ 'dialogs.iliasConnect.sync.lastRun' | translate }}:
                {{ settings(c).last_run_at | date: 'short' }}
              </p>

              <div class="ilias-actions">
                <button
                  nbButton
                  status="basic"
                  size="small"
                  [disabled]="!soapConfigured(c) || syncing[c.id]"
                  [nbTooltip]="'dialogs.iliasConnect.sync.runTooltip' | translate"
                  (click)="runSync(c)"
                >
                  <nb-icon [icon]="syncing[c.id] ? 'sync-outline' : 'refresh-outline'"></nb-icon>
                  {{ (syncing[c.id] ? 'dialogs.iliasConnect.sync.running' : 'dialogs.iliasConnect.sync.run') | translate }}
                </button>
              </div>
            </div>

            <div class="site-settings__footer">
              <button
                nbButton
                size="small"
                status="danger"
                outline
                [disabled]="busy[c.id] || c.connection_status === 'disconnected'"
                [nbSpinner]="busy[c.id]"
                [nbTooltip]="'dialogs.iliasConnect.disconnectTooltip' | translate"
                (click)="disconnect(c)"
              >
                <nb-icon icon="trash-2-outline"></nb-icon>
                {{ 'dialogs.iliasConnect.disconnect' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nb-card-body>

  <nb-card-footer>
    <button type="button" nbButton status="danger" outline (click)="close()">
      {{ 'buttons.close' | translate }}
    </button>
  </nb-card-footer>
</nb-card>
